// Task endpoints — Platform API CRUD on /task-instances + Tier 4 task_updated events.

import { apiv2Get, apiv2GetEnvelope, apiv2Create } from '@/lib/apiv2';
import { eventPost } from '@/lib/events';
import { currentUpdaterRole } from '@/config/program';
import { cleanActorOf, type CompletionActor } from '@/logic/task';
import { WITHDRAWAL_REQUEST_CODE } from '@/logic/withdrawalRequest';
import getOpenTasksForPatient from './actions/getOpenTasksForPatient';
import { SPRUCE_CALL_TEMPLATE } from '../../../../scripts/spruce-rows.mjs';

export { getOpenTasksForPatient };
const CLOSED_STATUSES = ['completed', 'cancelled', 'missed'] as const;
const TASK_QUEUE_LIMIT = 100;

// apiv2 has no available_at filter; hide latent (future-dated available_at) tasks client-side.
// null available_at = available now.
const isActionableNow = (t: TaskInstance): boolean => !t.available_at
  || new Date(t.available_at).getTime() <= Date.now();

export interface TaskInstance {
  task_id?: string | number;
  dedupe_key?: string;
  withings_user_id?: string | number;
  status?: string;
  priority?: string;
  owner_role?: string;
  assigned_to_type?: string;
  template_code?: string;
  clinical_domain?: string;
  available_at?: string | null;
  due_at?: string | null;
  created?: string | null; // apiv2 creation timestamp (ISO); `created_at` is the contract-spec alias.
  created_at?: string | null;
  modified?: string | null; // apiv2 last-modified timestamp (ISO); `updated_at` is the contract-spec alias.
  updated_at?: string;
  completion_notes?: string | null;
  [key: string]: unknown;
}

export interface OpenTasksOpts {
  assignedToType?: string;
  includeLatent?: boolean;
}

export interface PaginatedClosedTasks {
  tasks: TaskInstance[];
  totalCount: number;
  offset: number;
  limit: number;
  hasMore: boolean;
}

export interface TaskHistoryOpts {
  closedOffset?: number;
  closedLimit?: number;
  assignedToType?: string;
}

export interface TaskHistory {
  openTasks: TaskInstance[] | null;
  closedTasks: TaskInstance[];
  closedTotal: number;
  closedOffset: number;
  closedLimit: number;
  hasMoreClosed: boolean;
}

// Every status, one template. Mirrors WF17's own suppression query
// (`?withings_user_id=&template_code=&limit=5`) so the pre-send confirmation
// resolves the same answer the workflow will — a status filter here would miss
// the completed task that makes WF17 send nothing.
export async function getTasksByTemplateCode(
  withingsUserId: string | number,
  templateCode: string,
): Promise<TaskInstance[]> {
  const tasks = await apiv2Get('task-instances', {
    withings_user_id: String(withingsUserId),
    template_code: templateCode,
    limit: 5,
  }) as TaskInstance[] | undefined;
  return tasks || [];
}

export interface ClinicCallRead { tasks: TaskInstance[]; complete: boolean }
const CLINIC_CALL_READ_LIMIT = 500;

// Every status: a claim's re-check must see a call cancelled long ago, which the patient-file bundle can drop.
export async function getClinicCallTasks(withingsUserId: string | number): Promise<ClinicCallRead> {
  const env = await apiv2GetEnvelope('task-instances', {
    withings_user_id: String(withingsUserId),
    template_code: SPRUCE_CALL_TEMPLATE,
    sort: 'modified',
    order: 'desc',
    limit: CLINIC_CALL_READ_LIMIT,
  });
  const tasks = (env?.data as TaskInstance[]) || [];
  const count = Number(env?.meta?.count);
  return { tasks, complete: Number.isFinite(count) ? count <= tasks.length : tasks.length < CLINIC_CALL_READ_LIMIT };
}

// Every status, every period: a request left open on another period still holds the patient.
export async function getWithdrawalRequests(
  withingsUserId: string | number,
): Promise<TaskInstance[]> {
  const tasks = await apiv2Get('task-instances', {
    withings_user_id: String(withingsUserId),
    template_code: WITHDRAWAL_REQUEST_CODE,
    limit: 100,
  }) as TaskInstance[] | undefined;
  return tasks || [];
}

export async function getRecentClosedTasksForPatient(
  withingsUserId: string | number,
  opts: { offset?: number; limit?: number; assignedToType?: string } = {},
): Promise<PaginatedClosedTasks> {
  const { offset = 0, limit = 100, assignedToType } = opts;
  const env = await apiv2GetEnvelope('task-instances', {
    withings_user_id: String(withingsUserId),
    status: [...CLOSED_STATUSES],
    assigned_to_type: assignedToType,
    sort: 'modified',
    order: 'desc',
    offset,
    limit,
  });
  const tasks = (env?.data as TaskInstance[]) || [];
  const totalCount = env?.meta?.count ?? tasks.length;
  return {
    tasks, totalCount, offset, limit, hasMore: offset + tasks.length < totalCount,
  };
}

export async function getAllTasksForPatient(withingsUserId: string | number): Promise<TaskInstance[]> {
  const [open, closed] = await Promise.all([
    getOpenTasksForPatient(withingsUserId),
    getRecentClosedTasksForPatient(withingsUserId, { limit: 200 }),
  ]);
  return [...open, ...closed.tasks];
}

// Open tasks always fully loaded (safety: a clinician must never miss an open task).
// Closed paginated. Initial call (closedOffset=0) fetches both; subsequent only paginate closed.
export async function getTaskHistory(
  withingsUserId: string | number,
  { closedOffset = 0, closedLimit = 100, assignedToType }: TaskHistoryOpts = {},
): Promise<TaskHistory> {
  const closedPromise = apiv2GetEnvelope('task-instances', {
    withings_user_id: String(withingsUserId),
    status: [...CLOSED_STATUSES],
    assigned_to_type: assignedToType,
    sort: 'modified',
    order: 'desc',
    offset: closedOffset,
    limit: closedLimit,
  });

  const [openTasks, closed] = await Promise.all([
    closedOffset === 0
      ? getOpenTasksForPatient(withingsUserId, { assignedToType, includeLatent: true })
      : Promise.resolve(null),
    closedPromise,
  ]);
  const closedTasks = (closed?.data as TaskInstance[]) || [];
  const closedTotal = closed?.meta?.count ?? closedTasks.length;
  return {
    openTasks: openTasks ?? (closedOffset === 0 ? [] : null),
    closedTasks,
    closedTotal,
    closedOffset,
    closedLimit,
    hasMoreClosed: closedOffset + closedTasks.length < closedTotal,
  };
}

export async function getTaskQueue(roleFilter?: string | null): Promise<TaskInstance[]> {
  // Fetch wide (priority-sorted), drop latent tasks client-side, then re-apply the queue limit.
  const tasks = await apiv2Get('task-instances', {
    assigned_to_type: 'staff',
    status: ['pending', 'in_progress'],
    owner_role: roleFilter || undefined,
    sort: 'priority',
    order: 'asc',
    limit: 200,
  }) as TaskInstance[] | undefined;
  return (tasks || []).filter(isActionableNow).slice(0, TASK_QUEUE_LIMIT);
}

export async function getContentTasks(withingsUserId: string | number): Promise<TaskInstance[]> {
  const tasks = await apiv2Get('task-instances', {
    withings_user_id: String(withingsUserId),
    template_code: 'rec_content_read',
    sort: 'due_at',
    order: 'asc',
    limit: 100,
  });
  return tasks || [];
}

// ── Writes — POST /task-instances + Tier 4 task_updated ────────────────────

export interface TaskCreator {
  user_id?: string | null;
  firstname?: string | null;
  lastname?: string | null;
}

export interface CreateTaskPayload {
  withings_user_id: string | number;
  care_period_id?: string | number | null;
  owner_role?: string;
  task_type?: string;
  title: string;
  description?: string;
  clinical_domain?: string | null;
  priority?: string;
  due_at?: string | null;
  // Backing catalog template for ad-hoc staff tasks (default 'cc_custom_task').
  template_code?: string;
  // Stamped into context_json.created_by so the card can show "Created by <name>"
  // (mirrors the completed_by stamp WF17 writes on completion).
  created_by?: TaskCreator;
  // Idempotency key minted once at the call site (modal open) and held stable
  // across retries, so a double-submit hits apiv2's dedupe_key ON CONFLICT and
  // creates ONE task. Falls back to a generated key for non-UI callers.
  dedupe_key?: string;
}

// Sanctioned direct Tier-1 create (one of exactly four — platform-api.md "Three-tiered
// read pattern"): a manual cc_custom_task has no orchestration to run, and the UI needs
// the created row back. Mutations of existing tasks still go Tier-4 task_updated → WF17.
export async function createTask(task: CreateTaskPayload): Promise<unknown> {
  const createdBy = task.created_by && (task.created_by.firstname || task.created_by.lastname)
    ? {
      user_id: task.created_by.user_id ?? null,
      firstname: task.created_by.firstname ?? null,
      lastname: task.created_by.lastname ?? null,
    }
    : null;
  return apiv2Create('task-instances', {
    withings_user_id: task.withings_user_id,
    care_period_id: task.care_period_id,
    assigned_to_type: 'staff',
    owner_role: task.owner_role || 'care_coordinator',
    task_type: task.task_type || 'action',
    title: task.title,
    description: task.description || 'Manual task',
    clinical_domain: task.clinical_domain ?? null,
    priority: task.priority || 'normal',
    status: 'pending',
    due_at: task.due_at ?? null,
    trigger_source: 'manual',
    template_code: task.template_code ?? null,
    ...(createdBy ? { context_json: { created_by: createdBy } } : {}),
    dedupe_key: task.dedupe_key || `manual:${task.withings_user_id}:${Date.now()}`,
  });
}

// Canonical home is the pure tier (logic/task.ts) so other services can share it
// without a cross-service import; re-exported here for existing consumers.
export { cleanActorOf } from '@/logic/task';
export type { CompletionActor } from '@/logic/task';

// task_updated needs id_dev_user: without it apiv2's patient-program check 214s even a coordinator.
export async function completeTask(
  taskId: string | number,
  withingsUserId: string | number,
  notes = '',
  actor?: CompletionActor,
  // Extra registered WF17 change keys riding the same completion. No app caller today (the
  // Care Update delivery record was the last one); Phase 2's fax-status sync restores one.
  extraChanges?: Record<string, unknown>,
): Promise<unknown> {
  // extraChanges first: the endpoint's own semantics (status, notes) always win.
  const changes: Record<string, unknown> = { ...(extraChanges ?? {}), status: 'completed' };
  if (notes) changes.completion_notes = notes;
  // Identity rides in the `actor` wrapper (bare sent_by_* are guarded) — WF17 stamps context_json.completed_by.
  const cleanActor = cleanActorOf(actor);
  return eventPost('task_updated', {
    task_id: taskId,
    id_dev_user: withingsUserId,
    updated_by_role: actor?.role ?? currentUpdaterRole(),
    changes,
    ...(cleanActor ? { actor: cleanActor } : {}),
  });
}

// "Review baseline labs" Done WITH the care team's note: careteam_message_sent, which apiv2 takes from coordinator
// sessions only (a patient can never write a note); WF5 turns it into this task's completion for WF17.
export async function sendLabReviewCompletion(
  taskId: string | number,
  withingsUserId: string | number,
  { labNote, notes = '', actor }: { labNote: string; notes?: string; actor?: CompletionActor },
): Promise<{ status?: string } | undefined> {
  const cleanActor = cleanActorOf(actor);
  return eventPost('careteam_message_sent', {
    id_dev_user: withingsUserId,
    lab_review: { task_id: String(taskId) },
    lab_note: labNote,
    ...(notes ? { completion_notes: notes } : {}),
    updated_by_role: actor?.role ?? currentUpdaterRole(),
    ...(cleanActor ? { actor: cleanActor } : {}),
  });
}

// Close = cancelled/no-longer-needed, deliberately NOT 'completed' — WF17's completion
// cascades (labs-ready email, review-task creation) gate on status==='completed' and must
// not fire. Attribution rides completion_notes (WF17 stamps completed_by only on →completed).
export async function cancelTask(
  taskId: string | number,
  withingsUserId: string | number,
  notes = '',
  actor?: CompletionActor,
): Promise<unknown> {
  const changes: Record<string, unknown> = { status: 'cancelled' };
  if (notes) changes.completion_notes = notes;
  const cleanActor = cleanActorOf(actor);
  return eventPost('task_updated', {
    task_id: taskId,
    id_dev_user: withingsUserId,
    updated_by_role: actor?.role ?? currentUpdaterRole(),
    changes,
    ...(cleanActor ? { actor: cleanActor } : {}),
  });
}

// Undo a mistaken completion — reopen to the canonical open state. WF17 clears
// completed_at + strips context_json.completed_by when status leaves 'completed'
// (completion_source is not clearable through the adapter — apiv2 rejects '' on its
// enum — and is never rendered for open tasks); a reopened CANCELLED task passes
// clearNotes so the stale "Closed by …" note doesn't linger.
// toStatus lets a caller restore 'scheduled' (future available_at) instead of
// surfacing the task in the patient companion before its reveal date.
export async function reopenTask(
  taskId: string | number,
  withingsUserId: string | number,
  opts: { clearNotes?: boolean; toStatus?: 'pending' | 'scheduled'; actor?: CompletionActor } = {},
): Promise<unknown> {
  const cleanActor = cleanActorOf(opts.actor);
  return eventPost('task_updated', {
    task_id: taskId,
    id_dev_user: withingsUserId,
    updated_by_role: opts.actor?.role ?? currentUpdaterRole(),
    changes: { status: opts.toStatus ?? 'pending', ...(opts.clearNotes ? { completion_notes: '' } : {}) },
    ...(cleanActor ? { actor: cleanActor } : {}),
  });
}

export async function updateTaskRole(taskId: string | number, role: string, withingsUserId: string | number): Promise<unknown> {
  return eventPost('task_updated', {
    task_id: taskId,
    id_dev_user: withingsUserId,
    updated_by_role: currentUpdaterRole(),
    changes: { owner_role: role },
  });
}

export async function updateTaskPriority(taskId: string | number, priority: string, withingsUserId: string | number): Promise<unknown> {
  return eventPost('task_updated', {
    task_id: taskId,
    id_dev_user: withingsUserId,
    updated_by_role: currentUpdaterRole(),
    changes: { priority },
  });
}

export async function updateTaskNote(taskId: string | number, notes: string, withingsUserId: string | number): Promise<unknown> {
  return eventPost('task_updated', {
    task_id: taskId,
    id_dev_user: withingsUserId,
    updated_by_role: currentUpdaterRole(),
    changes: { completion_notes: notes },
  });
}
